import axios from 'axios';
import { DependencyList } from 'react';
import { isFunction } from 'src/utils/typeGuards';
import { FetchState, FETCH_ACTION_TYPES } from 'src/reducers/fetchReducer';
import useAsyncEffect from './useAsyncEffect';
import useSafeReducer from './useSafeReducer';

const { loading: SET_LOADING, setData: SET_DATA, setError: SET_ERROR } = FETCH_ACTION_TYPES;

export const initialState = {
  loading: false,
  error: false,
  data: null,
} satisfies FetchState<null>;

export type Fetcher<T> = () => Promise<T>;
export type Refetch<T> = (fetcher?: Fetcher<T>) => Promise<void>;
export type Reset = () => void;

const useFetchReducer = <T>(
  fetcher: Fetcher<T> | false | null,
  deps?: DependencyList
): [FetchState<T>, Refetch<T>, Reset] => {
  const [state, dispatch] = useSafeReducer<T>(initialState);

  const fetch = async (fetcher: Fetcher<T>) => {
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      const response: T = await fetcher();
      dispatch({ type: SET_DATA, payload: { data: response } });
    } catch (e) {
      if (!axios.isCancel(e)) {
        dispatch({ type: SET_ERROR, payload: { error: e } });
      }
    }
  };

  const refetch = async (refetcher?: Fetcher<T>) => {
    if (isFunction(refetcher)) {
      return fetch(refetcher);
    }
    if (isFunction(fetcher)) {
      return fetch(fetcher);
    }
  };

  const reset = () => {
    dispatch({ type: SET_DATA, payload: { data: null } });
  };

  useAsyncEffect(refetch, deps);

  return [state, refetch, reset];
};

export default useFetchReducer;
